import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { ButtonField } from '@wk/components-react16';
import { IconAlertTriangle } from '@tabler/icons-react';

import { loginRequest } from '@/config/msalConfig';
import {
  clearRedirectAuthError,
  getRedirectAuthError,
  setRedirectAuthError,
  shouldStartInteractiveLogin,
  subscribeToRedirectAuthError,
} from '@/lib/authRedirectState';

interface InteractiveAuthGateProps {
  children: ReactNode;
}

const InteractiveAuthGate = ({ children }: InteractiveAuthGateProps) => {
  const { accounts, inProgress, instance } = useMsal();
  const loginStarted = useRef(false);
  const redirectError = useSyncExternalStore(
    subscribeToRedirectAuthError,
    getRedirectAuthError,
    getRedirectAuthError,
  );

  useEffect(() => {
    if (
      !shouldStartInteractiveLogin({
        accountCount: accounts.length,
        isInteractionIdle: inProgress === InteractionStatus.None,
        loginStarted: loginStarted.current,
        redirectError,
      })
    ) {
      return;
    }

    loginStarted.current = true;
    void instance.loginRedirect(loginRequest).catch((error: unknown) => {
      loginStarted.current = false;
      setRedirectAuthError(error);
    });
  }, [accounts.length, inProgress, instance, redirectError]);

  if (redirectError) {
    const retryLogin = () => {
      if (loginStarted.current) {
        return;
      }

      loginStarted.current = true;
      clearRedirectAuthError();
      void instance.loginRedirect(loginRequest).catch((error: unknown) => {
        loginStarted.current = false;
        setRedirectAuthError(error);
      });
    };

    return (
      <main className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <section
          role="alert"
          className="w-full max-w-lg border border-gray-300 bg-white p-6 shadow-sm"
        >
          <div className="mb-4 flex items-center gap-3">
            <IconAlertTriangle
              aria-hidden="true"
              className="shrink-0 text-red-700"
              size={28}
            />
            <h1 className="text-xl font-bold text-gray-900">
              Unable to sign in
            </h1>
          </div>
          <p className="mb-5 text-sm text-gray-700">
            We could not complete authentication. Try signing in again. If the
            problem continues, contact support.
          </p>
          <ButtonField>
            <button type="button" onClick={retryLogin}>
              Try again
            </button>
          </ButtonField>
        </section>
      </main>
    );
  }

  if (inProgress !== InteractionStatus.None || accounts.length === 0) {
    return null;
  }

  return <>{children}</>;
};

export default InteractiveAuthGate;
